/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import React, { Component } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'util/PropTypes';
import { TabPanel } from '@ba/ba-ui-carbon-toolkit';

import Tabs from 'components/tabs/Tabs';
import Empty from 'components/empty/Empty';
import SlotsContainer from 'components/slotsContainer/SlotsContainer';
import NaturalLanguageText from 'components/nlt/NaturalLanguageText.js';
import StringResources from 'nls/StringResources';

import PropertiesContainer from 'components/properties/PropertiesContainer';

import _ from 'underscore';

import './canvasTabs.scss';

export const TAB_STATE = {
	EMPTY: 'empty',
	NO_VIS_SELECTED: 'no_vis_selected',
	SHOW_SLOTS: 'show_slots',
	SHOW_DATA: 'show_data'
};

@observer
class CanvasTabs extends Component {

	render() {
		if (!this.props.isDisplayed) {
			return null;
		}

		const widgetInfo = this._getWidgetInfo();

		const forwardedProps = _.omit(this.props, 'exploreVM', 'services');
		const slots = this.renderSlots(widgetInfo);
		const nlt = this.renderNlt(widgetInfo);
		const properties = this.renderProperties(widgetInfo);

		return (
			<Tabs {...forwardedProps}>
				{nlt}
				{slots}
				{properties}
			</Tabs>
		);
	}

	renderProperties(widgetInfo) {
		const tabState = widgetInfo.tabState === TAB_STATE.SHOW_SLOTS ? TAB_STATE.EMPTY : widgetInfo.tabState;
		let content = null;
		if (widgetInfo.tabState === TAB_STATE.SHOW_DATA) {
			const widget = widgetInfo.widget;
			const widgetType = widgetInfo.visualization.getType();
			const widgetId = widget.getId();

			content = (
				<PropertiesContainer
					widgetType={widgetType}
					widgetId={widgetId}
					dashboardApi={widgetInfo.dashboardApi}
					isDisplayed={this.props.isDisplayed}
				/>
			);
		} else {
			content = (
				<div className="card-layout-content__side-panel__empty-container">
					<Empty
						type={'default'}
						alignment={'left'}
						title={StringResources.get(`properties_panel_${ tabState }_title`)}
						description={StringResources.get(`properties_panel_${ tabState }_description`)}
					/>
				</div>
			);
		}

		return (
			<TabPanel id="properties" label={StringResources.get('propertiesPanelLabel')}>
				{ content }
			</TabPanel>
		);
	}

	renderSlots({ widget, dashboardApi }) {
		const content = (
			<SlotsContainer
				widget={widget}
				dashboardApi={dashboardApi}
			/>
		);
		return (
			<TabPanel id="slots" label={StringResources.get('slot_editor')}>
				{content}
			</TabPanel>
		);
	}

	renderNlt(widgetInfo) {
		const widgetLayoutModel = widgetInfo.widget && this.props.exploreVM.selectedWidgetLayoutModel;
		const tabState = widgetInfo.tabState === TAB_STATE.SHOW_SLOTS ? TAB_STATE.EMPTY : widgetInfo.tabState;

		// Force a re-render when this value changes. Can be removed when widget model is mobx.
		void(widgetLayoutModel && widgetLayoutModel.renderId);

		const content = (
			<div className="explore-details" walkme-data-id="nlt-details">
				<NaturalLanguageText
					model={widgetLayoutModel ? widgetLayoutModel.nlt : null}
					tabState={tabState}
				/>
			</div>
		);
		return (
			<TabPanel id="details" label={StringResources.get('card_details')}>
				{content}
			</TabPanel>
		);
	}

	_getWidgetInfo = () => {
		const widget = this.props.exploreVM.selectedWidget;
		const dashboardApi = this.props.exploreVM.layoutModel && this.props.exploreVM.layoutModel.getDashboardApi();
		const isCompareCard = this.props.exploreVM.isCompare();
		let isMappingComplete = null;
		let visErrorState = null;
		let visualization = null;
		let tabState = null;

		if (widget && widget.getId && widget.getVisApi && dashboardApi) {
			const canvas = dashboardApi.getCanvas();
			const content = canvas.getContent(widget.getId());
			visualization = content.getFeature('Visualization');
			visErrorState = content.getFeature('state').getError();
			const slots = visualization.getSlots();
			isMappingComplete = !visErrorState ? slots.isMappingComplete() : false;
		}

		if (isCompareCard) {
			if (!widget) {
				tabState = TAB_STATE.NO_VIS_SELECTED;
			} else if (widget && widget.getVisApi && !isMappingComplete) {
				tabState = TAB_STATE.SHOW_SLOTS;
			} else if (widget && !widget.getVisApi) {
				tabState = TAB_STATE.EMPTY;
			} else {
				tabState = TAB_STATE.SHOW_DATA;
			}
		} else {
			if (widget && widget.getVisApi && !visErrorState) {
				tabState = isMappingComplete ? TAB_STATE.SHOW_DATA : TAB_STATE.SHOW_SLOTS;
			} else {
				tabState = TAB_STATE.EMPTY;
			}
		}

		return {
			widget: widget,
			visualization: visualization,
			dashboardApi: dashboardApi,
			tabState: tabState
		};
	}
}

CanvasTabs.propTypes = {
	exploreVM: PropTypes.object.isRequired,
	services: PropTypes.object.isRequired,
	isDisplayed: PropTypes.boolean
};

export default CanvasTabs;


// WEBPACK FOOTER //
// ./src/js/components/canvasTabs/CanvasTabs.js